/**
 * 甲板：一块圆角矩形。走动就是把位置 clamp 回这个多边形。
 *
 * 因为船就是参考系、甲板是平的，所以：
 *  - 不需要重力；
 *  - 不需要地形碰撞；
 *  - 不需要世界坐标同步（位置本来就是船局部坐标）。
 * 这是整个项目最大的一处简化，别在这里引入物理引擎。
 */

export interface Deck {
  /** 半宽（米，X 方向） */
  halfWidth: number;
  /** 半长（米，Z 方向，船头在 -Z） */
  halfLength: number;
  /** 圆角半径（米） */
  cornerRadius: number;
}

/** 3.0 × 1.9 m 的甲板（船体加宽到 1.9 m 宽），留一点边给圆角。 */
export const DEFAULT_DECK: Deck = {
  halfWidth: 0.95,
  halfLength: 1.5,
  cornerRadius: 0.35,
};

export interface DeckPoint {
  x: number;
  z: number;
  /** 是否发生了钳制（用于贴边反馈/音效） */
  clamped: boolean;
}

/** 把点钳制到圆角矩形内。返回新对象，便于做纯函数测试。 */
export function clampToDeck(x: number, z: number, deck: Deck = DEFAULT_DECK): DeckPoint {
  const hw = deck.halfWidth;
  const hl = deck.halfLength;
  const r = Math.max(0, Math.min(deck.cornerRadius, Math.min(hw, hl)));

  let cx = Math.max(-hw, Math.min(hw, x));
  let cz = Math.max(-hl, Math.min(hl, z));
  let clamped = cx !== x || cz !== z;

  // 角落：把超出内矩形圆角范围的点推回圆弧上
  const ix = hw - r;
  const iz = hl - r;
  if (Math.abs(cx) > ix && Math.abs(cz) > iz) {
    const sx = Math.sign(cx);
    const sz = Math.sign(cz);
    const ox = cx - sx * ix;
    const oz = cz - sz * iz;
    const d = Math.hypot(ox, oz);
    if (d > r && d > 1e-9) {
      const s = r / d;
      cx = sx * ix + ox * s;
      cz = sz * iz + oz * s;
      clamped = true;
    }
  }

  return { x: cx, z: cz, clamped };
}

/** 点是否在甲板内（含边界，带浮点容差）。解析判断，不复用 clampToDeck。 */
export function isOnDeck(x: number, z: number, deck: Deck = DEFAULT_DECK): boolean {
  const eps = 1e-9;
  const hw = deck.halfWidth;
  const hl = deck.halfLength;
  const r = Math.max(0, Math.min(deck.cornerRadius, Math.min(hw, hl)));
  const ax = Math.abs(x);
  const az = Math.abs(z);

  if (ax > hw + eps || az > hl + eps) return false;
  if (ax <= hw - r + eps || az <= hl - r + eps) return true;

  const ox = ax - (hw - r);
  const oz = az - (hl - r);
  return ox * ox + oz * oz <= r * r + eps;
}

/** 两个玩家之间的软推出，避免重叠穿模（甲板太小，硬碰撞手感差）。 */
export function pushApart(
  ax: number,
  az: number,
  bx: number,
  bz: number,
  minDistance = 0.45,
): { ax: number; az: number; bx: number; bz: number } {
  const dx = bx - ax;
  const dz = bz - az;
  const d = Math.hypot(dx, dz);
  if (d >= minDistance || d < 1e-6) {
    return { ax, az, bx, bz };
  }
  const push = (minDistance - d) * 0.5;
  const nx = dx / d;
  const nz = dz / d;
  return {
    ax: ax - nx * push,
    az: az - nz * push,
    bx: bx + nx * push,
    bz: bz + nz * push,
  };
}

/** 船尾长凳：两个座位并排，凳子本身也是走动时的遮挡物 */
export const BENCH = { z: 0.95, halfWidth: 0.66, halfDepth: 0.18 } as const;

/** 甲板上的固定点：两人并排坐在船尾长凳上。 */
export const SEAT_SPOTS: readonly { readonly x: number; readonly z: number }[] = [
  { x: -0.32, z: BENCH.z },
  { x: 0.32, z: BENCH.z },
];

/** 站立时的身体半径，用来给长凳留出空隙 */
const BODY_RADIUS = 0.18;

/**
 * 站立走动的可用区域 = 甲板 − 长凳占位。
 * 坐下的瞬间会被直接放到座位上，所以不需要「绕过凳子走到座位」的路径。
 */
export function clampToWalkable(
  x: number,
  z: number,
  deck: Deck = DEFAULT_DECK,
  bench = BENCH,
): DeckPoint {
  const area = clampToDeck(x, z, deck);
  const frontZ = bench.z - bench.halfDepth - BODY_RADIUS;
  if (area.z <= frontZ) return area;
  return { x: area.x, z: frontZ, clamped: true };
}
